import {
  Audience,
  AudienceCardTypeOptions,
  AudienceConditionType,
  AudienceOutcome,
  AudiencePaymentTypeOptions,
  AudienceTrafficSourceOptions,
  DefaultMinBillingCycle,
} from './audiences';
import { BaseModel } from './base-model';
import { MFAType } from './campaign';
import {
  FunnelFulfillmentType,
  FunnelFulfillmentTypeLabels,
  FunnelInputCustomerPortalTypes,
  FunnelInputTypeEnum,
  SessionStartHybridStepTypes,
} from './funnel';
import { MultiSelectOption } from './merchant-profile';
import { CampaignProduct } from './product';
import { SessionType, SessionTypeLabels } from './session-enums';

export const ResolveBuyAgainDestinations: FunnelInputTypeEnum[] = [FunnelInputTypeEnum.Product, FunnelInputTypeEnum.Checkout];

// The portal opens on portal pages; legacy and support sessions on any session-start step the backend accepts.
export const ResolveViewOrderStepTypesBySession: { [key in SessionType]?: FunnelInputTypeEnum[] } = {
  [SessionType.CustomerPortal]: FunnelInputCustomerPortalTypes,
  [SessionType.LegacySupport]: SessionStartHybridStepTypes,
  [SessionType.SupportPortal]: SessionStartHybridStepTypes,
};

export enum ResolveRuleConditionType {
  FulfillmentType = 1,
  BillingCycle = 2,
  ThreeDs = 3,
  MerchantId = 4,
  CardType = 5,
  PaymentType = 6,
  Country = 7,
  State = 8,
  Campaign = 9,
  Product = 10,
  TrafficSource = 11,
}

export const ResolveRuleConditionLabels: { [key in ResolveRuleConditionType]: string } = {
  [ResolveRuleConditionType.FulfillmentType]: AudienceConditionType.FulfillmentType,
  [ResolveRuleConditionType.BillingCycle]: AudienceConditionType.BillingCycle,
  [ResolveRuleConditionType.ThreeDs]: '3DS Verification',
  [ResolveRuleConditionType.MerchantId]: 'Merchant ID',
  [ResolveRuleConditionType.CardType]: 'Card Type',
  [ResolveRuleConditionType.PaymentType]: 'Payment Type',
  [ResolveRuleConditionType.Country]: 'Country',
  [ResolveRuleConditionType.State]: 'State',
  [ResolveRuleConditionType.Campaign]: 'Campaign',
  [ResolveRuleConditionType.Product]: 'Product',
  [ResolveRuleConditionType.TrafficSource]: AudienceConditionType.TrafficSource,
};

// The audience filter fields a resolve rule carries (all audience conditions except survey response categories).
export type ResolveRuleConditionFields = Pick<
  Audience,
  | 'fulfillment_type'
  | 'min_billing_cycle'
  | 'max_billing_cycle'
  | 'use_billing_cycle'
  | 'is_3ds'
  | 'merchant_ids'
  | 'cc_types'
  | 'payment_types'
  | 'countries'
  | 'states'
  | 'crm_campaigns'
  | 'use_crm_campaign_filter'
  | 'use_product_filter'
  | 'traffic_sources'
>;

export interface ResolveAudienceRule extends Audience {
  resolve_settings: number;
  outcome: AudienceOutcome;
}

export type CreateResolveRulePayload = Pick<ResolveAudienceRule, 'name' | 'resolve_settings' | 'outcome'> & ResolveRuleConditionFields;

export const ResolveSettingsMissingMessage = 'Resolve settings could not be found for this account.';

export interface ResolveSettings extends BaseModel {
  show_view_order: boolean;
  show_buy_again: boolean;
  show_request_refund: boolean;
  prevent_duplicate_refunds: boolean;
  mfa_type: MFAType;
  session_type: SessionType;
  view_order_step_type: FunnelInputTypeEnum;
  buy_again_destination: FunnelInputTypeEnum.Product | FunnelInputTypeEnum.Checkout;
  refund_eligibility_window: number;
  max_auto_refund_amount: string;
  call_center_action: string | number;
  max_requests_per_window: number;
  velocity_window_days: number;
  merchant_profile: number;
  created: string;
  rules: ResolveAudienceRule[];
}

export enum ResolveActionType {
  ViewOrder = 'view_order',
  BuyAgain = 'buy_again',
}

export interface ResolveActionConfigMeta {
  title: string;
  description: string;
}

export const ResolveActionConfigs: { [key in ResolveActionType]: ResolveActionConfigMeta } = {
  [ResolveActionType.ViewOrder]: {
    title: 'View Order',
    description:
      'Choose where a matched order opens when a customer uses the View Order link on their digital receipt.',
  },
  [ResolveActionType.BuyAgain]: {
    title: 'Buy Again',
    description: 'Choose where eligible customers are sent when they repurchase the original item or offer.',
  },
};

// SessionTypeLabels, with the customer portal named as in the Resolve API doc.
export const ResolveSessionTypeLabels: string[] = SessionTypeLabels.map((label, type) =>
  type === SessionType.CustomerPortal ? 'Order Management Portal' : label
);

export enum RequestRefundTab {
  DisplayRules = 'display_rules',
  Guardrails = 'guardrails',
  Notifications = 'notifications',
}

export enum ResolvePreviewTab {
  DigitalReceipt = 'digital_receipt',
  OfferPreview = 'offer_preview',
}

export const ResolveRuleVisibleProductChipCount = 4;
export const ResolveRuleProductsDialogId = 'resolveRuleProductsDialog';

// The rule form holds dropdown options where the audience holds ids, and merchant IDs as typed text.
export type ResolveRuleConditionFormValue = Pick<Audience, 'fulfillment_type' | 'min_billing_cycle' | 'max_billing_cycle'> &
  Record<
    'cc_types' | 'payment_types' | 'countries' | 'states' | 'crm_campaigns' | 'traffic_sources',
    MultiSelectOption<number | string>[]
  > & {
    merchant_ids: string;
  };

export interface ResolveRuleChipLookups {
  countries: MultiSelectOption<number | string>[];
  campaigns: MultiSelectOption<number | string>[];
  products: CampaignProduct[];
}

// Rules list their conditions in enum order.
export const ResolveRuleConditionOrder = Object.keys(ResolveRuleConditionLabels).map(Number) as ResolveRuleConditionType[];

export const ResolveRuleFulfillmentOptions: MultiSelectOption[] = [FunnelFulfillmentType.Shippable, FunnelFulfillmentType.NotShippable].map(
  (id) => ({ id, text: FunnelFulfillmentTypeLabels[id] })
);

export const ResolveRuleCardTypeOptions: MultiSelectOption[] = AudienceCardTypeOptions.map(({ id, label }) => ({ id, text: label }));

export const ResolveRulePaymentTypeOptions: MultiSelectOption[] = AudiencePaymentTypeOptions.map(({ id, label }) => ({ id, text: label }));

export const ResolveRuleTrafficSourceOptions: MultiSelectOption[] = AudienceTrafficSourceOptions.map(({ id, label }) => ({
  id,
  text: label,
}));

export const ResolveRuleThreeDsText = 'Verified';

// What a rule sends for each condition it doesn't use, so editing a rule clears removed conditions.
export const ResolveRuleEmptyConditionFields: ResolveRuleConditionFields = {
  fulfillment_type: null,
  min_billing_cycle: DefaultMinBillingCycle,
  max_billing_cycle: null,
  use_billing_cycle: false,
  is_3ds: false,
  merchant_ids: null,
  cc_types: null,
  payment_types: null,
  countries: null,
  states: null,
  crm_campaigns: [],
  use_crm_campaign_filter: false,
  use_product_filter: false,
  traffic_sources: [],
};

// The rule form values each condition resets to when it is removed or swapped for another condition.
export const ResolveRuleConditionEmptyFormValues: { [key in ResolveRuleConditionType]: Partial<ResolveRuleConditionFormValue> } = {
  [ResolveRuleConditionType.FulfillmentType]: { fulfillment_type: null },
  [ResolveRuleConditionType.BillingCycle]: { min_billing_cycle: DefaultMinBillingCycle, max_billing_cycle: null },
  [ResolveRuleConditionType.ThreeDs]: {},
  [ResolveRuleConditionType.MerchantId]: { merchant_ids: null },
  [ResolveRuleConditionType.CardType]: { cc_types: [] },
  [ResolveRuleConditionType.PaymentType]: { payment_types: [] },
  [ResolveRuleConditionType.Country]: { countries: [] },
  [ResolveRuleConditionType.State]: { states: [] },
  [ResolveRuleConditionType.Campaign]: { crm_campaigns: [] },
  [ResolveRuleConditionType.Product]: {},
  [ResolveRuleConditionType.TrafficSource]: { traffic_sources: [] },
};
